<template>
  <StaffLayout section="Alert History (All Portals)" title="Alert History (All Portals)" hideHeader>
    <h1 class="py-3 mb-6 border-b flex">
      <div class="text-xl">
        <div class="font-bold">Alert History — {{ localPortal?.name ?? 'All Portals' }}</div>
      </div>
    </h1>

    <div class="flex flex-wrap gap-4 mb-4 items-end">
      <div>
        <label class="block text-sm font-medium mb-1">Start Date</label>
        <input type="date" v-model="startDate" class="border rounded px-2 py-1 text-sm" />
      </div>
      <div>
        <label class="block text-sm font-medium mb-1">End Date</label>
        <input type="date" v-model="endDate" class="border rounded px-2 py-1 text-sm" />
      </div>
      <div class="flex gap-2 items-center text-xs">
        <button class="text-blue-600 hover:underline" @click="setRange(30)">30 days</button>
        <button class="text-blue-600 hover:underline" @click="setRange(90)">90 days</button>
        <button class="text-blue-600 hover:underline" @click="setRange(182)">6 months</button>
        <button class="text-blue-600 hover:underline" @click="setRange(365)">12 months</button>
      </div>
    </div>

    <div class="mb-4">
      <label class="block text-sm font-medium mb-1">Filter by Portal</label>
      <div class="flex items-center gap-3">
        <PropertySelector v-model="localPortal" @selected="onPortalSelected" />
        <button
          v-if="selectedPortalId !== null"
          class="text-xs text-blue-600 hover:underline"
          @click="clearPortalFilter"
        >All Portals</button>
      </div>
    </div>

    <div class="mt-4">
      <Loading v-if="isLoading" />
      <div v-else>
        <div class="mt-4" v-if="sortedHistories?.length > 0">
          <div class="mb-4">
            <span>
              {{ sortedHistories?.length }} alerts sent in the last {{ dateRangeDays }} days</span>
              <span v-if="localPortal?.name"> by {{localPortal?.name}}</span>
              <span>.</span>
          </div>
          <table class="table-auto w-full border-2 shadow text-sm">
            <thead class="font-sm font-bold">
              <tr>
                <td class="pl-3"></td>
                <td class="pl-3 cursor-pointer" @click="changeSort(SortColumn.Client)">
                  Client <UpDownArrow v-if="columnToSort === SortColumn.Client" :is-down="isSortingDown" />
                </td>
                <td class="pl-3 cursor-pointer" @click="changeSort(SortColumn.Portal)">
                  Portal <UpDownArrow v-if="columnToSort === SortColumn.Portal" :is-down="isSortingDown" />
                </td>
                <td class="pl-3 py-2 cursor-pointer" @click="changeSort(SortColumn.SentBy)">
                  Sent By <UpDownArrow v-if="columnToSort === SortColumn.SentBy" :is-down="isSortingDown" />
                </td>
                <td class="px-3 cursor-pointer" @click="changeSort(SortColumn.DateSent)">
                  Date Sent <UpDownArrow v-if="columnToSort === SortColumn.DateSent" :is-down="isSortingDown" />
                </td>
                <td class="px-3 cursor-pointer text-center" @click="changeSort(SortColumn.Count)">
                  Count <UpDownArrow v-if="columnToSort === SortColumn.Count" :is-down="isSortingDown" />
                </td>
                <td class="px-3">Message</td>
              </tr>
            </thead>
            <template v-for="(history, i) in sortedHistories" :key="i">
              <tr
                class="font-xs font-mono border-bottom-2 hover:border-sky-800 hover:bg-sky-300 cursor-pointer"
                :class="{ 'bg-stone-200': i % 2 === 0 }"
                @click="selectClick(history)"
              >
                <td class="pl-3">
                  <div class="px-2">
                    <IHeroiconsOutlineChevronUp
                      class="mx-auto"
                      :class="{
                        'transform rotate-180': history.alertHistoryId !== selectedHistoryId,
                      }"
                    />
                  </div>
                </td>
                <td class="pl-3">{{ history.clientName }}</td>
                <td class="pl-3 text-xs">
                  <span :title="history.portalPropertyName ?? undefined">{{ history.portalSiteCode }}</span>
                </td>
                <td class="pl-3">
                  <span :title="history.senderPersonLoginEmail || undefined">
                    {{ [history.senderPersonFirstName, history.senderPersonLastName].filter(Boolean).join(" ") || history.senderPersonLoginEmail }}
                  </span>
                </td>
                <td class="px-3 whitespace-nowrap">
                  <span>{{ formatDateTime(history.dateSent) }}</span>
                </td>
                <td class="px-3 text-center">
                  <span :title="`Email: ${history.emailCount ?? 0}  SMS: ${history.smsCount ?? 0}  Push: ${history.pushCount ?? 0}  People: ${history.peopleCount ?? 0}`">
                    {{ history.recipientCount }}
                  </span>
                </td>
                <td class="px-3 max-w-xs">
                  <span
                    :title="history.messageText || undefined"
                    class="block whitespace-nowrap overflow-hidden text-ellipsis max-w-xs"
                  >{{ history.messageText }}</span>
                </td>
              </tr>
              <template v-if="history.alertHistoryId === selectedHistoryId">
                <tr>
                  <td :colspan="7" class="px-8 p-6 break-all">
                    <div class="font-bold">Message</div>
                    {{ history.messageText }}
                    <div class="mt-2 font-bold">Email List</div>
                    {{ history.emailList?.length === 0 ? "None." : history.emailList }}
                    <div class="mt-2 font-bold">SMS List</div>
                    {{ history.smsList?.length === 0 ? "None." : history.smsList }}
                    <div>
                      <a
                        href="#"
                        class="text-blue-600 hover:underline"
                        @click.prevent="navigateToDetail(history.alertHistoryId!)"
                        >More Detail</a
                      >
                    </div>
                  </td>
                </tr>
              </template>
            </template>
          </table>
        </div>
        <div v-else class="mb-2">
          <span>-- No Alert History found --</span>
        </div>
      </div>
    </div>
  </StaffLayout>
</template>

<script setup lang="ts">
import { useAlertStore, AlertHistoriesUiState } from "../../store/alertStore";
import { AlertHistoryAllDTO, PortalDTO } from "../../store/generatedFetchClient";
import StaffLayout from "../../layouts/StaffLayout.vue";
import PropertySelector from "../../components/PropertySelector.vue";
import { formatDateTime } from "../../helpers";
import { useBldgStore } from "../../store/bldgStore";

const router = useRouter();
const route = useRoute();

enum SortColumn {
  Client,
  Portal,
  SentBy,
  DateSent,
  Count,
}

const alertStore = useAlertStore();
const bldgStore = useBldgStore();
const allHistories = computed(() => alertStore.allHistories);
const MAX_RESULTS = 1000;
const isLoading = ref(false);

const selectedPortalId = ref<number | null>(null);
const localPortal = ref<PortalDTO | null>(null);

function onPortalSelected(portal: PortalDTO | null) {
  selectedPortalId.value = portal?.portalId ?? null;
}

function clearPortalFilter() {
  selectedPortalId.value = null;
  localPortal.value = null;
}

function getDateString(daysAgo: number): string {
  const d = new Date();
  d.setDate(d.getDate() - daysAgo);
  return d.toISOString().split("T")[0];
}

const startDate = ref<string>(getDateString(90));
const endDate = ref<string>(getDateString(0));

const loadedStart = ref<string>("");
const loadedEnd = ref<string>("");

function isSubsetOfLoaded(newStart: string, newEnd: string): boolean {
  if (!loadedStart.value || !loadedEnd.value) return false;
  return newStart >= loadedStart.value && newEnd <= loadedEnd.value;
}

watch([startDate, endDate], ([newStart, newEnd]) => {
  if (!newStart || !newEnd) return;
  if (isSubsetOfLoaded(newStart, newEnd)) return;
  loadData();
});

function setRange(days: number) {
  startDate.value = getDateString(days);
  endDate.value = getDateString(0);
}

const dateRangeDays = computed(() => {
  if (!startDate.value || !endDate.value) return 0;
  const ms = new Date(endDate.value).getTime() - new Date(startDate.value).getTime();
  return Math.round(ms / 86_400_000);
});

const columnToSort = ref<SortColumn>(SortColumn.DateSent);
const isSortingDown = ref(true);

const sortedHistories = computed(() => {
  let rows = allHistories.value?.slice() ?? [];
  if (selectedPortalId.value !== null) {
    rows = rows.filter((h) => h.portalId === selectedPortalId.value);
  }
  if (startDate.value) {
    const startMs = new Date(startDate.value + "T00:00:00").getTime();
    rows = rows.filter((h) => h.dateSent && new Date(h.dateSent).getTime() >= startMs);
  }
  if (endDate.value) {
    const endMs = new Date(endDate.value + "T23:59:59").getTime();
    rows = rows.filter((h) => h.dateSent && new Date(h.dateSent).getTime() <= endMs);
  }
  rows.sort((a, b) => {
    switch (columnToSort.value) {
      case SortColumn.Client:
        return (a.clientName ?? "").localeCompare(b.clientName ?? "");
      case SortColumn.Portal:
        return (a.portalSiteCode ?? "").localeCompare(b.portalSiteCode ?? "");
      case SortColumn.SentBy:
        return (a.senderPersonLoginEmail ?? "").localeCompare(b.senderPersonLoginEmail ?? "");
      case SortColumn.DateSent:
        return (a.dateSent ? new Date(a.dateSent).getTime() : 0) - (b.dateSent ? new Date(b.dateSent).getTime() : 0);
      case SortColumn.Count:
        return (a.recipientCount ?? 0) - (b.recipientCount ?? 0);
    }
  });
  if (isSortingDown.value) rows.reverse();
  return rows;
});

function changeSort(column: SortColumn) {
  if (column === columnToSort.value) {
    isSortingDown.value = !isSortingDown.value;
  } else {
    columnToSort.value = column;
    isSortingDown.value = false;
  }
}

onMounted(() => {
  const saved = alertStore.allHistoriesUiState;
  if (saved && alertStore.allHistories.length > 0) {
    loadedStart.value = saved.loadedStart;
    loadedEnd.value = saved.loadedEnd;
    startDate.value = saved.startDate;
    endDate.value = saved.endDate;
    selectedHistoryId.value = saved.selectedHistoryId;
    selectedPortalId.value = saved.selectedPortalId;
    localPortal.value = bldgStore.properties.find(p => p.portalId === saved.selectedPortalId) ?? null;
    columnToSort.value = saved.columnToSort;
    isSortingDown.value = saved.isSortingDown;
    alertStore.allHistoriesUiState = null;
    nextTick(() => requestAnimationFrame(() => window.scrollTo({ top: saved.scrollY, behavior: "instant" })));
  } else {
    alertStore.allHistoriesUiState = null;
    if (route.query.start) startDate.value = route.query.start as string;
    if (route.query.end) endDate.value = route.query.end as string;
    const queryPortalId = route.query.portalId ? Number(route.query.portalId) : null;
    if (queryPortalId) {
      selectedPortalId.value = queryPortalId;
      const found = bldgStore.properties.find(p => p.portalId === queryPortalId);
      if (found) {
        localPortal.value = found;
      } else {
        const unwatch = watch(() => bldgStore.properties, (props) => {
          if (props.length > 0) {
            localPortal.value = props.find(p => p.portalId === queryPortalId) ?? null;
            unwatch();
          }
        });
      }
    } else {
      selectedPortalId.value = bldgStore.property?.portalId ?? null;
      localPortal.value = bldgStore.property ?? null;
    }
    loadData();
  }
});

function navigateToDetail(alertHistoryId: number) {
  alertStore.allHistoriesUiState = {
    startDate: startDate.value,
    endDate: endDate.value,
    loadedStart: loadedStart.value,
    loadedEnd: loadedEnd.value,
    selectedHistoryId: selectedHistoryId.value,
    selectedPortalId: selectedPortalId.value,
    columnToSort: columnToSort.value,
    isSortingDown: isSortingDown.value,
    scrollY: window.scrollY,
  } satisfies AlertHistoriesUiState;
  router.push(`/staff/alert/detail/${alertHistoryId}`);
}

async function loadData() {
  isLoading.value = true;
  await alertStore.getAllHistory(
    MAX_RESULTS,
    startDate.value || undefined,
    endDate.value || undefined
  );
  loadedStart.value = startDate.value;
  loadedEnd.value = endDate.value;
  isLoading.value = false;
}

const selectedHistoryId = ref(-1);

function selectClick(history: AlertHistoryAllDTO) {
  selectedHistoryId.value =
    history.alertHistoryId === selectedHistoryId.value ? -1 : history.alertHistoryId!;
}
</script>
